import type { ReactElement } from "react"
import React, { useState } from "react"
import { useFormContext } from "react-hook-form"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import ValidationAdornment from "@/components/common/Autocomplete/ValidationAdornment"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { Classification } from "@/graphql/codegen/graphql"
import useDuplicateByFieldValidation from "@/hooks/useDuplicateByFieldValidation"
import useProductClassification from "@/screens/ManageProducts/components/ProductClasses/hooks/useProductClassification"
import useDebounce from "@/utils/useDebounce"

interface ClassificationSelectProps {
  categoryIds?: string[]
}

const ClassificationInput = ({ categoryIds }: ClassificationSelectProps): ReactElement => {
  const { control } = useFormContext()
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY_MS)

  const { productClassList } = useProductClassification({
    query: debouncedQuery.trim(),
    categoryIds,
    pause: debouncedQuery.trim().length < 2,
  })

  useDuplicateByFieldValidation<Classification>({
    query: debouncedQuery,
    list: productClassList,
    fieldName: "name",
    fieldLabel: "name",
    entityName: "Class",
  })

  const classificationNames = productClassList.map((classification: Classification) => classification.name)

  const handleInputChange = (value: string) => {
    setQuery(value)
  }
  return (
    <AutoComplete
      onInputChange={handleInputChange}
      label='Class Name'
      placeholder='Class Name'
      id='class-name-field'
      options={classificationNames}
      name='name'
      control={control}
      defaultEmptyState=''
      freeSolo
      listSelectionDisabled
      EndAdornment={query ? ValidationAdornment : undefined}
      required
      disabled={categoryIds?.length === 0}
    />
  )
}

export default ClassificationInput
